// Step 3: 参数输入 — Apple 浅色风格
import { useState } from 'react';
import {
  Moon, Lamp, CloudSun, SunMedium,
  ChevronDown, ChevronUp, Check, Ruler, Eye
} from 'lucide-react';
import { REGIONS, type RegionId } from './RegionStep';
import { SCENARIOS, type ScenarioId } from './ScenarioStep';

/** 环境光线选项 */
const LIGHTING_OPTIONS = [
  { id: 'dark', name: '暗室环境', nameEn: 'Dark Room', icon: Moon, nits: '300–600 nits', color: '#5856d6' },
  { id: 'indoor', name: '室内标准', nameEn: 'Indoor', icon: Lamp, nits: '600–1200 nits', color: '#34c759' },
  { id: 'semi_outdoor', name: '半户外', nameEn: 'Semi-Outdoor', icon: CloudSun, nits: '2000–4000 nits', color: '#ff9500' },
  { id: 'outdoor', name: '户外直射', nameEn: 'Full Outdoor', icon: SunMedium, nits: '5000+ nits', color: '#ff3b30' },
] as const;

export type LightingId = typeof LIGHTING_OPTIONS[number]['id'];

/** 安装方式 */
const INSTALL_METHODS = ['壁挂式', '落地式', '吊装式', '嵌入式', '移动安装'] as const;

export interface WizardParams {
  viewingDistance: number;    // 米
  lighting: LightingId;
  screenWidth?: number;      // 米
  screenHeight?: number;     // 米
  installMethod?: string;
  budget: 'premium' | 'standard' | 'economy';
}

interface ParameterStepProps {
  selectedRegion: RegionId;
  selectedScenario: ScenarioId;
  params: WizardParams;
  onParamsChange: (params: WizardParams) => void;
}

export function ParameterStep({ selectedRegion, selectedScenario, params, onParamsChange }: ParameterStepProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const regionName = REGIONS.find(r => r.id === selectedRegion)?.name ?? '';
  const scenarioName = SCENARIOS.find(s => s.id === selectedScenario)?.name ?? '';

  // 根据观看距离计算推荐间距
  const recommendedPitch = (params.viewingDistance / 3).toFixed(1);

  const update = (patch: Partial<WizardParams>) => {
    onParamsChange({ ...params, ...patch });
  };

  return (
    <div className="animate-in fade-in-0 duration-500">
      {/* 面包屑 */}
      <div className="flex items-center gap-1.5 text-[13px] text-[#86868b] mb-6">
        <span className="text-[#0071e3] font-medium">{regionName}</span>
        <span>›</span>
        <span className="text-[#0071e3] font-medium">{scenarioName}</span>
        <span>›</span>
        <span>项目参数</span>
      </div>

      <div className="max-w-2xl mx-auto space-y-8">
        {/* ① 观看距离 */}
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-[#0071e3]" />
            <h3 className="text-[15px] font-semibold text-[#1d1d1f]">观看距离</h3>
            <span className="ml-auto text-[13px] text-[#86868b]">
              推荐间距 ≈ P{recommendedPitch}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[12px] text-[#86868b] w-8">2m</span>
            <input
              type="range"
              min={2}
              max={100}
              step={1}
              value={params.viewingDistance}
              onChange={e => update({ viewingDistance: Number(e.target.value) })}
              className="flex-1 h-1.5 rounded-full appearance-none bg-gray-200
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5
                [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-[#0071e3]
                [&::-webkit-slider-thumb]:shadow-md
                [&::-webkit-slider-thumb]:cursor-pointer
              "
            />
            <span className="text-[12px] text-[#86868b] w-10">100m</span>
          </div>

          <div className="text-center mt-3">
            <span className="text-[32px] font-light text-[#1d1d1f] tabular-nums">
              {params.viewingDistance}
            </span>
            <span className="text-[15px] text-[#86868b] ml-1">米</span>
          </div>

          <p className="text-[11px] text-[#86868b] text-center mt-1">
            行业标准：最佳观看距离 = 间距 × 3（米）
          </p>
        </div>

        {/* ② 环境光线 */}
        <div className="bg-white rounded-2xl border border-gray-200/80 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lamp className="w-5 h-5 text-[#0071e3]" />
            <h3 className="text-[15px] font-semibold text-[#1d1d1f]">环境光线</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {LIGHTING_OPTIONS.map(opt => {
              const isSelected = params.lighting === opt.id;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.id}
                  onClick={() => update({ lighting: opt.id })}
                  className={`
                    relative flex flex-col items-center p-4 rounded-xl border transition-all duration-200
                    ${isSelected
                      ? 'border-[#0071e3] bg-[#0071e3]/[0.04] shadow-[0_0_0_1px_#0071e3]'
                      : 'border-gray-200/80 hover:border-gray-300'
                    }
                  `}
                >
                  {isSelected && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#0071e3] flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  )}
                  <Icon className="w-6 h-6 mb-2" style={{ color: isSelected ? '#0071e3' : opt.color }} />
                  <span className={`text-[13px] font-medium ${
                    isSelected ? 'text-[#0071e3]' : 'text-[#1d1d1f]'
                  }`}>
                    {opt.name}
                  </span>
                  <span className="text-[11px] text-[#86868b] mt-0.5">{opt.nits}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ③ 可选参数（折叠） */}
        <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-[#86868b]" />
              <span className="text-[15px] font-medium text-[#1d1d1f]">可选参数</span>
              <span className="text-[12px] text-[#86868b] ml-1">屏幕尺寸 · 安装方式 · 预算</span>
            </div>
            {showAdvanced
              ? <ChevronUp className="w-4 h-4 text-[#86868b]" />
              : <ChevronDown className="w-4 h-4 text-[#86868b]" />
            }
          </button>

          {showAdvanced && (
            <div className="px-5 pb-5 space-y-5 border-t border-gray-100 pt-5">
              {/* 屏幕尺寸 */}
              <div>
                <label className="text-[13px] font-medium text-[#1d1d1f] mb-2 block">屏幕尺寸</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] text-[#86868b]">宽</span>
                    <input
                      type="number"
                      step="0.1"
                      value={params.screenWidth ?? ''}
                      onChange={e => update({ screenWidth: e.target.value ? Number(e.target.value) : undefined })}
                      placeholder="4.0"
                      className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 text-[14px] text-[#1d1d1f]
                        placeholder:text-gray-300 focus:outline-none focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3]/20"
                    />
                    <span className="text-[12px] text-[#86868b]">m</span>
                  </div>
                  <span className="text-[#86868b]">×</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] text-[#86868b]">高</span>
                    <input
                      type="number"
                      step="0.1"
                      value={params.screenHeight ?? ''}
                      onChange={e => update({ screenHeight: e.target.value ? Number(e.target.value) : undefined })}
                      placeholder="2.5"
                      className="w-20 px-3 py-1.5 rounded-lg border border-gray-200 text-[14px] text-[#1d1d1f]
                        placeholder:text-gray-300 focus:outline-none focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3]/20"
                    />
                    <span className="text-[12px] text-[#86868b]">m</span>
                  </div>
                </div>
              </div>

              {/* 安装方式 */}
              <div>
                <label className="text-[13px] font-medium text-[#1d1d1f] mb-2 block">安装方式</label>
                <select
                  value={params.installMethod ?? ''}
                  onChange={e => update({ installMethod: e.target.value || undefined })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[14px] text-[#1d1d1f]
                    focus:outline-none focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3]/20 bg-white"
                >
                  <option value="">不限</option>
                  {INSTALL_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              {/* 预算偏好 */}
              <div>
                <label className="text-[13px] font-medium text-[#1d1d1f] mb-2 block">预算偏好</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { id: 'premium', label: '高端', desc: 'TOP+ 优先' },
                    { id: 'standard', label: '标准', desc: '性价比均衡' },
                    { id: 'economy', label: '经济', desc: '成本优先' },
                  ] as const).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => update({ budget: opt.id })}
                      className={`
                        px-3 py-2.5 rounded-xl border text-center transition-all duration-200
                        ${params.budget === opt.id
                          ? 'border-[#0071e3] bg-[#0071e3]/[0.04] shadow-[0_0_0_1px_#0071e3]'
                          : 'border-gray-200/80 hover:border-gray-300'
                        }
                      `}
                    >
                      <span className={`text-[14px] font-medium block ${
                        params.budget === opt.id ? 'text-[#0071e3]' : 'text-[#1d1d1f]'
                      }`}>
                        {opt.label}
                      </span>
                      <span className="text-[11px] text-[#86868b]">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ParameterStep;
